import React, { useState, useMemo, useEffect } from 'react';
import { formatDealValue } from '../../lib/dealValue';
import { fmtLabel } from '../../lib/acronyms';
import {
  TrendingUp, Plus, ChevronRight, ChevronDown, Building2,
  User, Users, Clock, MapPin, FileText, DollarSign,
  ExternalLink, Eye, Share2, Loader2, Search, Lock, Check
} from 'lucide-react';
import { normalizeTech } from '../../lib/techNormalize';
import { authFetch } from '../../lib/authFetch';

// Helper to safely format capacity (handles object {amount, unit} or string/number)
const formatCapacity = (capacity, unit) => {
  if (!capacity) return null;
  if (typeof capacity === 'object' && capacity !== null) {
    const amount = capacity.amount || capacity.value;
    const capUnit = capacity.unit || unit || 'MW';
    return amount ? `${amount} ${capUnit}` : null;
  }
  if (typeof capacity === 'string') {
    if (/[a-zA-Z]/.test(capacity)) return capacity;
    return `${capacity} ${unit || 'MW'}`;
  }
  if (typeof capacity === 'number') {
    return `${capacity} ${unit || 'MW'}`;
  }
  return null;
};

// Helper to safely format value/price
const safeFormatValue = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && value !== null) {
    let amount = value.amount || value.value;
    if (!amount) return null;
    const currency = value.currency || '$';
    let unit = value.unit || 'M';
    // Normalize raw numbers that slipped through
    if (amount >= 1_000_000_000) { amount = amount / 1_000_000_000; unit = 'B'; }
    else if (amount >= 1_000_000) { amount = amount / 1_000_000; unit = 'M'; }
    const formatted = amount % 1 === 0 ? amount.toLocaleString() : amount.toFixed(1);
    return `${currency}${formatted}${unit}`;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return null;
};

/**
 * DashboardDealsFeed - Compilation of all deals (standalone + legacy counterpart-embedded)
 *
 * Props:
 * - deals: Array of deal objects
 * - counterparts: Array of counterpart objects for linking
 * - theme: 'light' | 'dark'
 * - themeClasses: Theme CSS classes object
 * - user: Current user object (for admin checks)
 * - dealStatuses: Array of status options
 * - dealTechnologies: Array of technology options
 * - dealTypes: Array of deal type options
 * - dealRoles: Array of role options
 * - onSelectCounterpart: Callback when counterpart is clicked
 * - filterCounterpart: Filter by counterpart ID ('all' for no filter)
 * - onAddDeal: Callback to add new deal
 * - screenerFilters: Object with filter arrays
 * - initialExpandedDealId: ID of deal to expand initially
 * - onExpandedDealChange: Callback when expanded deal changes
 * - onShareDeal: Callback to share deal
 * - onViewDeal: Callback to view deal detail
 * - smartSearchDealIds: Array of deal IDs from smart search
 * - searchQuery: Current search query string
 * - isSignedIn: Whether user is authenticated (for Tier 0 gating)
 * - onSignUpPrompt: Callback to show sign-up prompt for Tier 0 users
 */
const DashboardDealsFeed = ({
  deals = [],
  counterparts = [],
  theme = 'light',
  themeClasses = {},
  user,
  dealStatuses = [],
  dealTechnologies = [],
  dealTypes = [],
  dealRoles = [],
  onSelectCounterpart,
  filterCounterpart = 'all',
  onAddDeal,
  screenerFilters = {},
  initialExpandedDealId,
  onExpandedDealChange,
  onShareDeal,
  onViewDeal,
  smartSearchDealIds: searchDealIds,
  searchQuery = '',
  isSignedIn = true,
  onSignUpPrompt,
  dealCompareList = [],
  setDealCompareList,
}) => {
  const [sortOrder, setSortOrder] = useState('newest');
  const [showAll, setShowAll] = useState(false);
  const [expandedDealKey, setExpandedDealKey] = useState(null);
  const [creatingCounterpart, setCreatingCounterpart] = useState(null);

  // Sync expanded deal with parent when initialExpandedDealId changes
  useEffect(() => {
    if (initialExpandedDealId) {
      setExpandedDealKey(initialExpandedDealId);
      setShowAll(true);
      setTimeout(() => {
        const dealElement = document.getElementById(`deal-${initialExpandedDealId}`);
        if (dealElement) {
          dealElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      if (onExpandedDealChange) onExpandedDealChange(null);
    }
  }, [initialExpandedDealId, onExpandedDealChange]);

  // Handle clicking on an unmatched counterpart - create it and enrich
  const handleUnmatchedCounterpartClick = async (name, category) => {
    if (creatingCounterpart === name) return;
    setCreatingCounterpart(name);
    try {
      const response = await authFetch('/api/counterparts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          counterpart: { name, type: category || 'company', content_status: 'published' }
        })
      });

      let counterpartData = null;

      if (response.ok) {
        const data = await response.json();
        counterpartData = data.counterpart;
      } else if (response.status === 409) {
        const coreName = name.split(/\s+(Corporation|Corp|Inc|Ltd|Limited|LLC|Co|Company)/i)[0].trim();
        const existingRes = await fetch(`/api/counterparts?search=${encodeURIComponent(coreName)}&limit=5`);
        if (existingRes.ok) {
          const existingData = await existingRes.json();
          const nameLower = name.toLowerCase();
          const match = existingData.counterparts?.find(c => {
            const cpLower = c.name.toLowerCase();
            return cpLower === nameLower || cpLower.includes(coreName.toLowerCase()) || nameLower.includes(cpLower);
          });
          if (match) {
            counterpartData = match;
          }
        }
      }

      if (counterpartData) {
        sessionStorage.setItem('counterparts_cache_bust', Date.now());
        const needsEnrichment = !counterpartData.enrichment?.summary && !counterpartData.investmentPreferences?.summary;
        const canEnrich = user?.publicMetadata?.admin ||
          user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.com') ||
          user?.primaryEmailAddress?.emailAddress?.endsWith('@ganiz.net');

        if (needsEnrichment && canEnrich) {
          onSelectCounterpart?.({ ...counterpartData, _isEnriching: true, summary: 'Loading summary...' });

          const enrichRes = await authFetch('/api/enrich-counterpart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ counterpartId: counterpartData.id, name, industry: 'infrastructure', counterpartType: counterpartData.type || 'company' })
          });

          if (enrichRes.ok) {
            const enrichData = await enrichRes.json();
            if (enrichData.success) {
              const enrichedCp = {
                ...counterpartData,
                investmentPreferences: enrichData.investmentPreferences,
                summary: enrichData.investmentPreferences?.summary,
                _isEnriching: false
              };
              onSelectCounterpart?.(enrichedCp);
            } else {
              const errorMsg = enrichData.message?.includes('429') || enrichData.message?.includes('quota')
                ? 'Quota exceeded. Try again later.'
                : 'Summary unavailable.';
              onSelectCounterpart?.({ ...counterpartData, summary: errorMsg, _enrichError: true });
            }
          } else {
            onSelectCounterpart?.({ ...counterpartData, summary: 'Summary unavailable.', _enrichError: true });
          }
        } else {
          onSelectCounterpart?.(counterpartData);
        }
      }
    } catch (err) {
      console.error('Error creating counterpart:', err);
    } finally {
      setCreatingCounterpart(null);
    }
  };

  // Compile all deals
  const allDeals = useMemo(() => {
    const compiledDeals = [];

    deals.forEach(deal => {
      const linkedCounterparts = deal.linkedCounterparts ||
        (deal.deal_counterparts || []).map(dc => ({
          counterpartId: dc.counterpart?.id || dc.counterpart_id,
          name: dc.counterpart?.name || dc.name || '',
          category: dc.counterpart?.type || dc.type || 'company',
          role: dc.role || 'other',
          stake: dc.stake,
          isLead: dc.is_lead
        }));

      compiledDeals.push({
        ...deal,
        linkedCounterparts,
        isStandalone: true,
        counterpartNames: linkedCounterparts.map(lc => lc.name).join(', ') || '',
      });
    });

    compiledDeals.sort((a, b) => {
      const dateA = new Date(a.announcement_date || a.signing_date || a.date || a.created_at || a.createdAt || 0);
      const dateB = new Date(b.announcement_date || b.signing_date || b.date || b.created_at || b.createdAt || 0);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return compiledDeals;
  }, [deals, sortOrder]);

  // Filter deals
  const filteredDeals = useMemo(() => {
    let result = allDeals;

    if (searchDealIds !== null) {
      const idSet = new Set(searchDealIds);
      result = result.filter(d => idSet.has(d.id));
    }

    if (filterCounterpart !== 'all') {
      result = result.filter(deal =>
        deal.linkedCounterparts?.some(lc => lc.counterpartId === filterCounterpart)
      );
    }

    if (screenerFilters.sectors?.length > 0) {
      result = result.filter(deal => {
        const techs = Array.isArray(deal.technology) ? deal.technology : [deal.technology || deal.asset_type || deal.sector];
        const normalizedTechs = techs.filter(Boolean).map(t => normalizeTech(t)?.toLowerCase()).filter(Boolean);
        if (normalizedTechs.length === 0) return false;
        return screenerFilters.sectors.some(s => normalizedTechs.includes(s.toLowerCase()));
      });
    }

    if (screenerFilters.geographies?.length > 0) {
      result = result.filter(deal => {
        // Check each geography field individually for exact match (case-insensitive)
        const dealCountries = (Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).map(c => c.toLowerCase().trim());
        const dealRegion = (deal.region || '').toLowerCase().trim();
        const dealLocation = (deal.location || '').toLowerCase().trim();
        return screenerFilters.geographies.some(g => {
          const filterVal = g.toLowerCase().trim();
          return dealCountries.some(dc => dc && (dc === filterVal || dc.includes(filterVal) || filterVal.includes(dc))) ||
                 (dealRegion && (dealRegion === filterVal || dealRegion.includes(filterVal) || filterVal.includes(dealRegion))) ||
                 (dealLocation && (dealLocation === filterVal || dealLocation.includes(filterVal) || filterVal.includes(dealLocation)));
        });
      });
    }

    if (screenerFilters.dealTypes?.length > 0) {
      result = result.filter(deal => {
        const dt = (deal.dealType || deal.deal_type || deal.type || '').toLowerCase();
        return screenerFilters.dealTypes.some(t => dt === t.toLowerCase() || dt.includes(t.toLowerCase()));
      });
    }

    if (screenerFilters.statuses?.length > 0) {
      result = result.filter(deal => {
        const status = (deal.status || '').toLowerCase();
        return screenerFilters.statuses.some(s => status === s.toLowerCase());
      });
    }

    if (screenerFilters.counterparts?.length > 0) {
      result = result.filter(deal => {
        const dealCounterpartIds = (deal.linkedCounterparts || []).map(lc => lc.counterpartId);
        const dbCounterpartIds = (deal.deal_counterparts || []).map(dc => dc.counterpart?.id);
        const allIds = [...dealCounterpartIds, ...dbCounterpartIds].filter(Boolean);
        return screenerFilters.counterparts.some(cpId => allIds.includes(cpId));
      });
    }

    if (screenerFilters.timePeriod && screenerFilters.timePeriod !== 'all') {
      const days = parseInt(screenerFilters.timePeriod);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      result = result.filter(deal => {
        const dealDate = new Date(deal.date || deal.createdAt || deal.created_at);
        return dealDate >= cutoff;
      });
    }

    return result;
  }, [allDeals, filterCounterpart, screenerFilters, searchDealIds]);

  // Tier 0 (unregistered): cap at 20 deals (first page), no "show all"
  const TIER0_LIMIT = 20;
  const initialLimit = isSignedIn ? 10 : Math.min(TIER0_LIMIT, filteredDeals.length);
  const displayedDeals = !isSignedIn
    ? filteredDeals.slice(0, TIER0_LIMIT)
    : showAll ? filteredDeals : filteredDeals.slice(0, 10);

  const formatDealSize = (deal) => {
    const formatted = formatDealValue(deal.financials?.dealValue);
    if (formatted) return formatted;
    const size = deal.size || deal.value;
    if (!size) return null;
    if (typeof size === 'number') return formatDealValue({ amount: size, unit: 'M', currency: 'USD' });
    return size;
  };

  return (
    <div className={`${themeClasses.card} rounded-2xl border ${themeClasses.border} overflow-hidden shadow-sm`}>
      {/* Header */}
      <div className={`px-6 py-5 border-b ${themeClasses.border} ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${theme === 'dark' ? 'bg-slate-700' : 'bg-white shadow-sm'}`}>
              <TrendingUp size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className={`text-xl font-semibold tracking-tight ${themeClasses.text}`}>Deal Flow</h3>
              <p className={`text-[15px] ${themeClasses.textMuted}`}>
                {filteredDeals.length} deal{filteredDeals.length !== 1 ? 's' : ''}{filterCounterpart !== 'all' && ` of ${allDeals.length}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className={`text-[15px] px-3 py-2 rounded-xl border-2 font-medium ${theme === 'dark' ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-200 bg-white text-slate-700'} focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400`}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
            {onAddDeal && (
              <button
                onClick={onAddDeal}
                className="p-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-colors shadow-sm"
                title="Add Deal"
              >
                <Plus size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Deals List */}
      {filteredDeals.length > 0 ? (
        <div className={`divide-y ${themeClasses.border}`}>
          {displayedDeals.map((deal, i) => {
            const dealKey = deal.id || `deal-${i}`;
            const isExpanded = expandedDealKey === dealKey;
            const linkedCps = deal.linkedCounterparts || [];
            const counterpartCount = linkedCps.length;

            return (
              <div
                key={dealKey}
                id={`deal-${deal.id}`}
                className={`transition-all duration-200 ${isExpanded ? (theme === 'dark' ? 'bg-slate-800/60' : 'bg-blue-50/60') : ''}`}
              >
                {/* Collapsed View */}
                <div
                  className={`px-6 py-5 ${themeClasses.cardHover} cursor-pointer group`}
                  onClick={() => setExpandedDealKey(isExpanded ? null : dealKey)}
                >
                  <div className="flex items-center gap-4">
                    {/* Compare checkbox */}
                    {setDealCompareList && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDealCompareList(prev => prev.includes(deal.id)
                            ? prev.filter(id => id !== deal.id)
                            : prev.length < 3 ? [...prev, deal.id] : prev
                          );
                        }}
                        className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                          dealCompareList.includes(deal.id)
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : theme === 'dark' ? 'border-slate-600 hover:border-blue-500' : 'border-slate-300 hover:border-blue-500'
                        }`}
                      >
                        {dealCompareList.includes(deal.id) && <Check size={12} />}
                      </button>
                    )}
                    {/* Date badge */}
                    <div className="flex flex-col items-center flex-shrink-0 w-14">
                      {(() => {
                        const dealDate = deal.announcement_date || deal.signing_date || deal.date || deal.created_at || deal.createdAt;
                        if (dealDate) {
                          const dateObj = new Date(dealDate);
                          if (!isNaN(dateObj.getTime())) {
                            return (
                              <div className={`text-center ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                                <div className="text-xl font-bold leading-tight">
                                  {dateObj.toLocaleDateString('en-GB', { day: 'numeric' })}
                                </div>
                                <div className="text-[13px] uppercase leading-tight font-medium">
                                  {dateObj.toLocaleDateString('en-GB', { month: 'short' })}
                                </div>
                                <div className="text-[13px] leading-tight">
                                  {dateObj.getFullYear()}
                                </div>
                              </div>
                            );
                          }
                        }
                        return (
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${theme === 'dark' ? 'bg-blue-700/20' : 'bg-blue-50'}`}>
                            <TrendingUp size={20} className="text-blue-600" />
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Title row */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <h4 className={`text-sm font-semibold tracking-tight leading-snug ${themeClasses.text} ${isExpanded ? '' : 'line-clamp-2'} flex-1 min-w-0 group-hover:text-blue-600 transition-colors`}>{deal.headline || deal.description}</h4>
                        {/* Inline badges */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {deal.status && deal.status !== 'announced' && (
                            <span className={`text-[13px] px-2.5 py-1 rounded-lg font-medium ${
                              deal.status === 'closed' ? 'bg-green-100 text-green-700' :
                              deal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              deal.status === 'tender' ? 'bg-amber-100 text-amber-700' :
                              deal.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {dealStatuses.find(s => s.value === deal.status)?.label || deal.status}
                            </span>
                          )}
                          {(Array.isArray(deal.technology) ? deal.technology : [deal.technology]).filter(Boolean).map(tech => (
                            <span key={tech} className="text-[13px] px-2.5 py-1 rounded-lg font-medium bg-amber-100 text-amber-700">
                              {dealTechnologies.find(t => t.value === tech)?.label || tech}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        {counterpartCount > 0 && (
                          <span className={`text-[15px] ${themeClasses.textMuted}`}>
                            {linkedCps.map(lc => lc.name).join(' • ')}
                          </span>
                        )}
                        {deal.location && deal.location !== 'null' && (
                          <>
                            <span className={`text-[15px] ${themeClasses.textMuted}`}>•</span>
                            <span className={`text-[15px] ${themeClasses.textMuted}`}>{deal.location}</span>
                          </>
                        )}
                        {formatCapacity(deal.capacity, deal.capacity_unit || deal.capacityUnit) && (
                          <>
                            <span className={`text-[15px] ${themeClasses.textMuted}`}>•</span>
                            <span className="text-[15px] font-semibold text-blue-600">
                              {formatCapacity(deal.capacity, deal.capacity_unit || deal.capacityUnit)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {formatDealSize(deal) && (
                        <div className="text-right">
                          <p className={`text-lg font-bold ${themeClasses.text}`}>{formatDealSize(deal)}</p>
                          <p className={`text-[13px] ${themeClasses.textMuted}`}>deal size</p>
                        </div>
                      )}
                      <div className={`p-1.5 rounded-lg transition-colors ${theme === 'dark' ? 'group-hover:bg-slate-700' : 'group-hover:bg-slate-100'}`}>
                        <ChevronDown
                          size={18}
                          className={`${themeClasses.textMuted} transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-6 pb-6 pt-0">
                    <div className={`ml-14 pl-6 border-l-2 ${theme === 'dark' ? 'border-slate-600' : 'border-blue-200'}`}>
                      <div className={`p-5 rounded-2xl ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'} border ${themeClasses.border} shadow-sm`}>

                        {/* Headline */}
                        {deal.headline && deal.headline !== deal.description && (
                          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>{deal.headline}</h3>
                        )}

                        {/* Status & Type Badges */}
                        <div className="flex items-center gap-2 mb-5 flex-wrap">
                          {deal.status && (
                            <span className={`text-sm px-3 py-1.5 rounded-xl font-medium ${
                              deal.status === 'closed' ? 'bg-green-100 text-green-700' :
                              deal.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              deal.status === 'announced' ? 'bg-purple-100 text-purple-700' :
                              deal.status === 'tender' ? 'bg-amber-100 text-amber-700' :
                              deal.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {dealStatuses.find(s => s.value === deal.status)?.label || deal.status}
                            </span>
                          )}
                          {(deal.dealType || deal.deal_type) && (
                            <span className={`text-sm px-3 py-1.5 rounded-xl font-medium ${theme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                              {dealTypes.find(t => t.value === (deal.dealType || deal.deal_type))?.label || fmtLabel(deal.dealType || deal.deal_type)}
                            </span>
                          )}
                          {(Array.isArray(deal.technology) ? deal.technology : [deal.technology]).filter(Boolean).map(tech => (
                            <span key={tech} className="text-sm px-3 py-1.5 rounded-xl font-medium bg-amber-100 text-amber-700">
                              {dealTechnologies.find(t => t.value === tech)?.label || tech}
                            </span>
                          ))}
                          {deal.stage && (
                            <span className="text-sm px-3 py-1.5 rounded-xl font-medium bg-indigo-100 text-indigo-700">
                              {deal.stage}
                            </span>
                          )}
                        </div>

                        {/* Key Metrics Row */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                          {formatDealSize(deal) && (
                            <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}>Deal Size</p>
                              <p className={`text-sm font-semibold ${themeClasses.text}`}>{formatDealSize(deal)}</p>
                            </div>
                          )}
                          {formatCapacity(deal.capacity, deal.capacity_unit || deal.capacityUnit) && (
                            <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}>Capacity</p>
                              <p className={`text-sm font-semibold ${themeClasses.text}`}>
                                {formatCapacity(deal.capacity, deal.capacity_unit || deal.capacityUnit)}
                              </p>
                            </div>
                          )}
                          {deal.number_of_assets && (
                            <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}># Assets</p>
                              <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.number_of_assets}</p>
                            </div>
                          )}
                          {deal.annual_generation && (
                            <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs font-medium ${themeClasses.textMuted} mb-1`}>Annual Gen.</p>
                              <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.annual_generation}</p>
                            </div>
                          )}
                        </div>

                        {/* Financials Section */}
                        {deal.financials && typeof deal.financials === 'object' && Object.keys(deal.financials).length > 0 && (
                          <div className="mb-4">
                            <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                              <DollarSign size={12} /> Financials
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {deal.financials.enterpriseValue && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Enterprise Value</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{safeFormatValue(deal.financials.enterpriseValue)}</p>
                                </div>
                              )}
                              {deal.financials.equityValue && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Equity Value</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{safeFormatValue(deal.financials.equityValue)}</p>
                                </div>
                              )}
                              {deal.financials.debt && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Debt</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{safeFormatValue(deal.financials.debt)}</p>
                                </div>
                              )}
                              {deal.financials.irr && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>IRR</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{typeof deal.financials.irr === 'object' ? deal.financials.irr.value || deal.financials.irr.amount : deal.financials.irr}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Timeline Section */}
                        {(deal.announcement_date || deal.signing_date || deal.expected_closing || deal.actual_closing || deal.cod) && (
                          <div className="mb-4">
                            <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                              <Clock size={12} /> Timeline
                            </p>
                            <div className="flex flex-wrap gap-3">
                              {deal.announcement_date && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                  <span className={`text-xs ${themeClasses.text}`}>Announced: {deal.announcement_date}</span>
                                </div>
                              )}
                              {deal.signing_date && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                  <span className={`text-xs ${themeClasses.text}`}>Signed: {deal.signing_date}</span>
                                </div>
                              )}
                              {deal.expected_closing && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                                  <span className={`text-xs ${themeClasses.text}`}>Expected Close: {deal.expected_closing}</span>
                                </div>
                              )}
                              {deal.actual_closing && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                  <span className={`text-xs ${themeClasses.text}`}>Closed: {deal.actual_closing}</span>
                                </div>
                              )}
                              {deal.cod && (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                                  <span className={`text-xs ${themeClasses.text}`}>COD: {deal.cod}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Geography Section */}
                        {((Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).length > 0 || deal.region || deal.state || deal.city || deal.project_site || deal.project_name) && (
                          <div className="mb-4">
                            <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                              <MapPin size={12} /> Location
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {deal.project_name && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Project</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.project_name}</p>
                                </div>
                              )}
                              {(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).filter(c => c && c !== 'null').length > 0 && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Country</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{(Array.isArray(deal.country) ? deal.country : (deal.country ? [deal.country] : [])).join(', ')}</p>
                                </div>
                              )}
                              {deal.region && deal.region !== 'null' && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Region</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.region}</p>
                                </div>
                              )}
                              {deal.state && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>State</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.state}</p>
                                </div>
                              )}
                              {deal.city && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>City</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.city}</p>
                                </div>
                              )}
                              {deal.project_site && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Site</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.project_site}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* PPA Info */}
                        {deal.ppa_info && typeof deal.ppa_info === 'object' && Object.keys(deal.ppa_info).length > 0 && (
                          <div className="mb-4">
                            <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                              <FileText size={12} /> PPA Details
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {deal.ppa_info.offtaker && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Offtaker</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.ppa_info.offtaker}</p>
                                </div>
                              )}
                              {deal.ppa_info.term && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Term</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.ppa_info.term}</p>
                                </div>
                              )}
                              {deal.ppa_info.price && (
                                <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <p className={`text-xs ${themeClasses.textMuted}`}>Price</p>
                                  <p className={`text-sm font-semibold ${themeClasses.text}`}>{deal.ppa_info.price}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* People Section */}
                        {Array.isArray(deal.people) && deal.people.length > 0 && (
                          <div className="mb-4">
                            <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2 flex items-center gap-1`}>
                              <Users size={12} /> Key People
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {deal.people.map((person, idx) => (
                                <div key={idx} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                  <div className={`w-6 h-6 rounded-full ${theme === 'dark' ? 'bg-slate-600' : 'bg-white'} flex items-center justify-center`}>
                                    <User size={12} className="text-blue-700" />
                                  </div>
                                  <div>
                                    <p className={`text-xs font-medium ${themeClasses.text}`}>{person.name}</p>
                                    {person.role && <p className={`text-[10px] ${themeClasses.textMuted}`}>{person.role}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        {deal.notes && (
                          <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'} mb-4`}>
                            <p className={`text-xs ${themeClasses.textMuted} mb-1`}>Notes</p>
                            <p className={`text-sm ${themeClasses.text}`}>{deal.notes}</p>
                          </div>
                        )}

                        {/* Source */}
                        {(deal.sourceUrl || deal.source_url || deal.source_title) && (
                          <div className="mb-4 flex items-center gap-3">
                            {(deal.sourceUrl || deal.source_url) && (
                              <a
                                href={deal.sourceUrl || deal.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                <ExternalLink size={12} />
                                {deal.source_title || 'View Source'}
                              </a>
                            )}
                            {deal.source_date && (
                              <span className={`text-xs ${themeClasses.textMuted}`}>Published: {deal.source_date}</span>
                            )}
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                          {onViewDeal && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewDeal(deal);
                              }}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-blue-700 text-white hover:bg-blue-800"
                            >
                              <Eye size={14} />
                              View Full
                            </button>
                          )}
                          {onShareDeal && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onShareDeal(deal);
                              }}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                theme === 'dark'
                                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                              }`}
                            >
                              <Share2 size={14} />
                              Share
                            </button>
                          )}
                        </div>

                        {/* Linked Counterparts Section */}
                        {linkedCps.length > 0 && (
                          <div>
                            <p className={`text-xs font-medium ${themeClasses.textMuted} mb-2`}>
                              Counterparts ({linkedCps.length})
                            </p>
                            <div className="space-y-2">
                              {linkedCps.map((lc, idx) => {
                                const cp = counterparts.find(c => c.id === lc.counterpartId);
                                const roleLabel = dealRoles.find(r => r.value === lc.role)?.label || lc.role;
                                const isCreating = creatingCounterpart === lc.name;
                                return (
                                  <div
                                    key={idx}
                                    className={`flex items-center gap-2 p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'} cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (cp) {
                                        onSelectCounterpart?.(cp);
                                      } else if (lc.name) {
                                        handleUnmatchedCounterpartClick(lc.name, lc.category);
                                      }
                                    }}
                                  >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-slate-600' : 'bg-white'}`}>
                                      {isCreating ? (
                                        <Loader2 size={14} className="text-blue-700 animate-spin" />
                                      ) : (
                                        <Building2 size={14} className="text-blue-700" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                                        {lc.name}
                                        {lc.needsReview && (
                                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">Review</span>
                                        )}
                                        {!cp && !isCreating && (
                                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">+ Add</span>
                                        )}
                                      </p>
                                      <p className={`text-xs ${themeClasses.textMuted}`}>{lc.category}</p>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${theme === 'dark' ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                                      {roleLabel}
                                    </span>
                                    <ChevronRight size={14} className={themeClasses.textMuted} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Import button for enrichment deals */}
                        {!deal.isStandalone && onAddDeal && (
                          <div className={`mt-4 pt-4 border-t ${themeClasses.border}`}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const enrichmentDeal = {
                                  description: deal.description,
                                  headline: deal.headline || deal.description,
                                  dealType: deal.type === 'acquisition' ? 'acquisition' :
                                            deal.type === 'sale' ? 'divestiture' :
                                            deal.type === 'partnership' ? 'joint_venture' :
                                            deal.type === 'development' ? 'development' : 'other',
                                  date: deal.date,
                                  country: deal.location || (Array.isArray(deal.country) ? deal.country.join(', ') : (deal.country || '')),
                                  capacity: deal.capacity ? String(deal.capacity).replace(/[^0-9.]/g, '') : '',
                                  technology: deal.technology,
                                  linkedCounterparts: linkedCps.map(lc => ({
                                    name: lc.name,
                                    role: lc.role || 'other',
                                    type: lc.category || 'company'
                                  })),
                                  sourceUrl: deal.source,
                                  _importedFromEnrichment: true
                                };
                                onAddDeal(enrichmentDeal);
                              }}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium transition-colors"
                            >
                              <Plus size={16} />
                              Import to Database
                            </button>
                            <p className={`text-xs ${themeClasses.textMuted} mt-2 text-center`}>
                              Import this deal to add full details.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-8 text-center">
          <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-slate-800' : 'bg-slate-100'} flex items-center justify-center mx-auto mb-3`}>
            {searchQuery ? <Search size={20} className={themeClasses.textMuted} /> : <TrendingUp size={20} className={themeClasses.textMuted} />}
          </div>
          <p className={`text-sm ${themeClasses.textMuted}`}>
            {searchQuery ? 'No deals match your search' : filterCounterpart !== 'all' ? 'No deals for this counterpart' : 'No deals available'}
          </p>
          <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
            {searchQuery ? 'Try a different search term' : filterCounterpart !== 'all' ? 'Try selecting a different counterpart' : 'Deal data may still be loading. Try refreshing the page.'}
          </p>
        </div>
      )}

      {/* Show More Button */}
      {filteredDeals.length > (isSignedIn ? 10 : TIER0_LIMIT) && (
        <div className={`p-3 border-t ${themeClasses.border}`}>
          {isSignedIn ? (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full text-center text-sm font-medium text-blue-700 hover:text-blue-800"
            >
              {showAll ? 'Show Less' : `Show All ${filteredDeals.length} Deals`}
            </button>
          ) : (
            <button
              onClick={() => onSignUpPrompt?.()}
              className="w-full flex items-center justify-center gap-2 text-sm font-medium text-slate-500 hover:text-blue-700"
            >
              <Lock size={14} />
              Sign up to see all {filteredDeals.length} deals
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardDealsFeed;
